import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, withModifiers as _withModifiers, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-561ade93"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "mb-6" }
const _hoisted_2 = { class: "mb-4 text-sm" }
const _hoisted_3 = ["href"]
const _hoisted_4 = { class: "mb-4 text-sm" }
const _hoisted_5 = ["href"]

import { onMounted, ref, watch, nextTick } from "vue";
import { VueRecaptcha } from "vue-recaptcha";
import { pushEvent } from "@/services/dataLayerLogin";
import { contextPath, country } from "@ocm/services/ocm-object";
import GlobalError from "@/components/GlobalError.vue";
import SharedButton from "@SharedVueComponents/SharedButton.vue";
import SharedForm from "@SharedVueComponents/SharedForm/SharedForm.vue";
import SharedTextfield from "@SharedVueComponents/SharedTextfield.vue";
import { passwordValidator } from "@/services/validation";
import { store } from "@/state/store";
import { authenticate } from "@/services/ocmAuthentication";
import { useI18n } from "vue-i18n";
import Error from "@/components/Error.vue";
import BasicSkeleton from "@SharedVueComponents/BasicSkeleton/BasicSkeleton.vue";

declare global {
	interface Window {
		tub18950abtest: boolean;
	}
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LoginForm',
  setup(__props, { expose: __expose }) {

const { t } = useI18n();
let section: "login" | "registration" = "login";
const captchaError = ref("");
const email = ref("");
const password = ref("");
const captcha = ref<{
	response: string | null;
	key: string | null;
	loaded: boolean;
}>({
	response: null,
	key: window.ocm?.captchaOptions?.publickey,
	loaded: false,
});
const renderCaptcha = ref(false);
const waitingForResponse = ref(false);

const invisibleReCaptcha = ref<InstanceType<typeof VueRecaptcha> | null>(null);
const loginForm = ref<InstanceType<typeof SharedForm> | null>(null);
const loginFormIsVisible = ref(false);

watch(
	() => store.isLogin,
	() => {
		section = store.isLogin ? "login" : "registration";
		resetForm();
	}
);

onMounted(() => {
	abTestInitABTest();

	section = store.isLogin ? "login" : "registration";
	if (!store.isModal) loginFormIsVisible.value = true;

	if (captcha.value.key) {
		let intervalCount = 0;
		const intervalID = setInterval(() => {
			const captchaIsLoaded = !!window.grecaptcha;
			intervalCount++;

			if (captchaIsLoaded) {
				captcha.value.loaded = true;
				clearInterval(intervalID);
			} else if (intervalCount >= 150) {
				captchaError.value = t(`errorMessages.httpError`);
				clearInterval(intervalID);
				pushEvent("captcha_error_init", captchaError.value);
			}
		}, 200);
	}
});

function handleOnFocus(fieldName: string) {
	abTestTrack("Focus", fieldName);
}

function handleOnChange(fieldName: string, errorMessage: string | undefined) {
	errorMessage && pushEvent(`change_${fieldName}_error`, errorMessage);

	abTestSetFirstStepHasError(!!errorMessage);
	abTestTrack("Change", fieldName);
}

function handleOnSubmit(fieldName: string, errorMessage: string | undefined) {
	errorMessage && pushEvent(`submit_${fieldName}_error`, errorMessage);
}

function handleOnInput() {
	resetGlobalError();
}

function resetForm() {
	resetValidation();
	resetGlobalError();
	password.value = "";
}

function resetValidation() {
	if (loginForm.value) {
		loginForm.value.resetFormValidation();
	}
}

function resetGlobalError() {
	store.globalErrorMessages = [];
	store.ocmErrorMessages = [];
	store.httpErrorStatusCode = null;
}

function clearFormData() {
	resetForm();
	email.value = "";
}

function requestFirstLogin() {
	// TUB-18186 show sceleton instead of form the first time modal is opened
	// reason: prevent autofill freeze in Chrome-Browsers
	// timeout Value as defined in _popup.scss .animated
	window.setTimeout(() => {
		loginFormIsVisible.value = true;
	}, 400);
}

async function handleSubmitClick() {
	if (abTestisFirstPage()) return;

	if (captcha.value.key) {
		renderCaptcha.value = true;
		await nextTick();
		try {
			invisibleReCaptcha.value.execute();
		} catch (error) {
			captchaError.value = t(`errorMessages.httpError`);
			pushEvent("captcha_error_submit", captchaError.value);
		}
		return;
	}
	submitForm();
}

function handleCaptchaResponse(response: string) {
	captcha.value.response = response;
	invisibleReCaptcha.value?.reset();
	submitForm();
}

function submitForm() {
	waitingForResponse.value = true;

	const authData = {
		j_username: email.value,
		j_password: password.value,
		"g-recaptcha-response": captcha.value.response,
	};

	authenticate(authData, section).finally(() => {
		waitingForResponse.value = false;
	});
}

function ocmButtonClick() {
	store.isSocialLogin = false;
	let trackingAction = "OCMButton";
	if (store.isModal) {
		trackingAction = store.isLogin ? "LoginBtnClick" : "RegistrationBtnClick";
	}
	pushEvent(trackingAction);
}

function passwortForgottenLink() {
	return `${window.location.origin}${contextPath}/my-account/forgotten-password`;
}

// *************************************************** SSO AB-TEST START**********************************
// todo TUB-18950: after AB Test
// Remove
// - this part
// - all Funktions Calls for AB-Test
// - check for <template ... data-abtest-remove-template-NOT/AND-content
// - autocomplete ind email sharedTextfield
// OTHER
// - AppleLogin.vue, FacebookLogin.vue, GoogleLogin.vue
// - SharedTextfiled.vue -> remove setFocus()

const abTestFirstStepHasError = ref(false);
const abTestButtonDisabled = ref(false);
const passwordField = ref<InstanceType<typeof SharedTextfield> | null>(null);
// - remove also ref in component instance

// Folgende Varible muss im CMS-Cockpit in einem Script-Tag gesetzt werden
// Oben auf der Seite !!
function abTestInitABTest() {
	store.abTestIsTestPage = window.tub18950abtest;
	store.abTestFirstStep = store.abTestIsTestPage;
}

function abTestisFirstPage(): boolean {
	return store.abTestIsTestPage && store.abTestFirstStep;
}

function abTestSetFirstStepHasError(isTrue: boolean) {
	abTestFirstStepHasError.value = isTrue;
}

function abTestFinishFirstStep() {
	store.isSocialLogin = false;
	pushEvent("MailButton");

	if (abTestFirstStepHasError.value || !email.value) return;

	abTestButtonDisabled.value = true;
	// change Dokument-Contents after a wink
	window.setTimeout(() => {
		store.abTestFirstStep = false;

		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;

		document.querySelector(".c-cms-login-image")?.classList.add("hidden");
		document.querySelector(".c-login-layer__headline")?.classList.add("hidden");
		document.querySelector(".c-login-layer__headline + h3")?.classList.add("hidden");
		document.querySelector(".benefit-items-wrapper")?.classList.add("hidden");
		document.querySelector(".c-header-meta-nav-wrapper")?.classList.add("hidden");
		document
			.querySelector(".c-header")
			?.setAttribute("style", "border-bottom: 2px solid var(--color-gray-40); height: 75px; top: -75px;");
		document.querySelector(".c-main-content")?.setAttribute("style", "min-height: unset;");
		const footnote = document.querySelectorAll(".ocm-footnote1");
		footnote.forEach((node) => node.classList.add("hidden"));
		const footerColumn = document.querySelectorAll(".c-main-footer__column");
		footerColumn[2]?.classList.add("hidden");
		footerColumn[3]?.classList.add("hidden");
		const abTestElements = document.querySelectorAll(".tub18950abtest");
		abTestElements.forEach((node) => node.classList.add("hidden"));

		keepFooterOnBottom();

		nextTick(() => {
			passwordField.value?.setFocus();
		});
	}, 200);
}

function keepFooterOnBottom() {
	const wrapper: HTMLElement | null = document.querySelector(".js-offcanvas-innerwrapper");
	const footer = document.querySelector("footer");
	if (!wrapper || !footer) return;

	wrapper.style.position = "relative";
	wrapper.style.minHeight = "100vh";

	footer.style.position = "absolute";
	footer.style.bottom = "0";
	footer.style.width = "100%";

	let footerHeight = 0;
	const setWrapperPaddingBottom = () => {
		if (footerHeight !== footer.offsetHeight) {
			footerHeight = footer.offsetHeight;
			wrapper.style.paddingBottom = `${footerHeight}px`;
		}
	};
	setWrapperPaddingBottom();
	window.addEventListener("resize", setWrapperPaddingBottom);
}

function abTestTrack(eventType: string, fieldName: string) {
	// nur auf der 2ten seiten, wenn es ab Test ist und nur email
	if (store.abTestIsTestPage && !store.abTestFirstStep && fieldName === "email") pushEvent(`Mail${eventType}`);
}

// :autocomplete="store.abTestIsTestPage && !store.abTestFirstStep ? 'off' : 'username'"

// *************************************************** SSO AB-TEST END**********************************

__expose({
	clearFormData,
	requestFirstLogin,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!!captcha.value.key && renderCaptcha.value)
      ? (_openBlock(), _createBlock(_unref(VueRecaptcha), {
          key: 0,
          ref_key: "invisibleReCaptcha",
          ref: invisibleReCaptcha,
          size: "invisible",
          language: _unref(country),
          "load-recaptcha-script": false,
          sitekey: captcha.value.key,
          onVerify: handleCaptchaResponse
        }, null, 8, ["language", "sitekey"]))
      : _createCommentVNode("", true),
    (loginFormIsVisible.value)
      ? (_openBlock(), _createBlock(SharedForm, {
          key: 1,
          ref_key: "loginForm",
          ref: loginForm,
          "show-error": false,
          "error-message": "",
          "no-submit-button-gap": true,
          onSubmit: _withModifiers(handleSubmitClick, ["prevent"])
        }, {
          submitButton: _withCtx(() => [
            (!_unref(store).abTestIsTestPage)
              ? (_openBlock(), _createBlock(SharedButton, {
                  key: 0,
                  type: "submit",
                  class: "mt-2 w-full",
                  disabled: waitingForResponse.value || !(!!captcha.value.key === captcha.value.loaded),
                  onClick: ocmButtonClick
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t(_unref(store).isLogin ? "login.submit_button" : "registration.submit_button")), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : (!_unref(store).abTestFirstStep)
                ? (_openBlock(), _createBlock(SharedButton, {
                    key: 1,
                    type: "submit",
                    class: "mt-2 w-full",
                    disabled: waitingForResponse.value || !(!!captcha.value.key === captcha.value.loaded),
                    onClick: ocmButtonClick
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(store).isLogin ? "Anmelden" : "Weiter"), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _createVNode(SharedTextfield, {
              modelValue: email.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
              "pattern-error-message": _ctx.$t('validation.email'),
              required: "",
              "required-error-message": _ctx.$t('validation.email'),
              name: "j_username",
              "display-name": _ctx.$t('email-display-name'),
              type: "email",
              autocomplete: "username",
              placeholder: _ctx.$t(_unref(store).isLogin ? 'login.email_input_placeholder' : 'registration.email_input_placeholder'),
              class: "mb-4",
              "forced-error-message": _unref(store).httpErrorStatusCode === 422 && !_unref(store).isSocialLogin ? ' ' : '',
              "validate-on-change": "",
              onFocus: _cache[1] || (_cache[1] = ($event: any) => (handleOnFocus('email'))),
              onInput: handleOnInput,
              onChange: _cache[2] || (_cache[2] = (_, data) => handleOnChange('email', data.errorMessage)),
              onFormSubmit: _cache[3] || (_cache[3] = (_, data) => handleOnSubmit('email', data.errorMessage))
            }, null, 8, ["modelValue", "pattern-error-message", "required-error-message", "display-name", "placeholder", "forced-error-message"]),
            (!_unref(store).abTestFirstStep)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(SharedTextfield, {
                    ref_key: "passwordField",
                    ref: passwordField,
                    modelValue: password.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((password).value = $event)),
                    pattern: _unref(passwordValidator),
                    "pattern-error-message": _ctx.$t('validation.password'),
                    required: "",
                    "required-error-message": _ctx.$t('validation.password'),
                    name: "j_password",
                    "display-name": _ctx.$t('password-display-name'),
                    type: "password",
                    placeholder: 
						_ctx.$t(_unref(store).isLogin ? 'login.password_input_placeholder' : 'registration.password_input_placeholder')
					,
                    class: "mb-4",
                    "forced-error-message": _unref(store).httpErrorStatusCode === 422 && !_unref(store).isSocialLogin ? ' ' : '',
                    "validate-on-change": "",
                    onFocus: _cache[5] || (_cache[5] = ($event: any) => (handleOnFocus('password'))),
                    onInput: handleOnInput,
                    onChange: _cache[6] || (_cache[6] = (_, data) => handleOnChange('password', data.errorMessage)),
                    onFormSubmit: _cache[7] || (_cache[7] = (_, data) => handleOnSubmit('password', data.errorMessage))
                  }, null, 8, ["modelValue", "pattern", "pattern-error-message", "required-error-message", "display-name", "placeholder", "forced-error-message"]),
                  (_unref(store).isLogin)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createElementVNode("div", _hoisted_2, [
                          _createElementVNode("a", {
                            class: "underline",
                            href: passwortForgottenLink(),
                            onClick: _cache[8] || (_cache[8] = ($event: any) => (_unref(pushEvent)('PasswordForgotten')))
                          }, _toDisplayString(_ctx.$t("login.forgot_password")), 9, _hoisted_3)
                        ]),
                        _createElementVNode("div", _hoisted_4, [
                          _createTextVNode(_toDisplayString(_ctx.$t("login.app_user")) + " ", 1),
                          _createElementVNode("a", {
                            class: "underline",
                            href: passwortForgottenLink(),
                            onClick: _cache[9] || (_cache[9] = ($event: any) => (_unref(pushEvent)('AppPassword')))
                          }, _toDisplayString(_ctx.$t("login.create_password")), 9, _hoisted_5),
                          _createTextVNode(". ")
                        ])
                      ], 64))
                    : _createCommentVNode("", true)
                ], 64))
              : _createCommentVNode("", true),
            (!_unref(store).isSocialLogin)
              ? (_openBlock(), _createBlock(GlobalError, {
                  key: 1,
                  class: "mt-2"
                }))
              : _createCommentVNode("", true),
            (captchaError.value)
              ? (_openBlock(), _createBlock(Error, {
                  key: 2,
                  class: "mt-2"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(captchaError.value), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_unref(store).abTestFirstStep)
              ? (_openBlock(), _createBlock(SharedButton, {
                  key: 3,
                  type: "submit",
                  class: "mt-2 w-full",
                  disabled: abTestButtonDisabled.value,
                  onClick: abTestFinishFirstStep
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t(_unref(store).isLogin ? "login.submit_button" : "registration.submit_button")), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["onSubmit"]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(BasicSkeleton, { class: "mb-4 h-15" }),
      (!_unref(store).abTestFirstStep)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(BasicSkeleton, { class: "mb-4 h-15" }),
            (_unref(store).isLogin)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(BasicSkeleton, { class: "mb-4 h-5" }),
                  _createVNode(BasicSkeleton, { class: "mb-4 h-5" })
                ], 64))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true),
      _createVNode(BasicSkeleton, { class: "mt-2 h-11.5" })
    ], 512), [
      [_vShow, !loginFormIsVisible.value]
    ])
  ]))
}
}

})